import { PriceAdjustment } from 'components/secondaryMarket/live/sidePanel/quoted/priceAdjustement/PriceAdjustment';
import { connect } from 'react-redux';
import type { FormOwnProps } from 'shared/connect/connect';
import { liveInputFormActions } from 'state/secondaryMarket/live/form/input/inputForm.actions';
import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import { liveThunks } from 'state/secondaryMarket/live/live.thunk';
import {
  getLiveQuotePriceType,
  getLiveQuoteTradingPriceFormatted,
  getLiveQuoteXMarginPlusUpfront,
} from 'state/secondaryMarket/live/quote/liveQuote.selector';
import { liveReferentialSelectors } from 'state/secondaryMarket/live/referential/liveReferential.selector';
import type { SecondaryPriceType } from 'state/secondaryMarket/productSearch';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

type PriceAdjustmentStateProps = {
  priceType: SecondaryPriceType;
  currency?: string;
  xMarginPlusUpfront: number;
  customizedUpfront?: number;
  customizedXMarginAdjustment?: number;
  tradingPrice: number;
  customizedTradingPriceAdjustment?: number;
  canAdjustPrice: boolean;

  comment: string | undefined;
};

type PriceAdjustmentDispatchProps = {
  onCommentChanged: (value: string) => void;
  onCustomizedXMarginAdjustmentChanged: (value?: number) => void;
  onCustomizedUpfrontChanged: (value?: number) => void;
  onCustomizedTradingPriceAdjustmentChanged: (value?: number) => void;
  onResetCustomizedUpfront: () => void;
  onResetCustomizedXMargin: () => void;
};

type PriceAdjustmentOwnProps = FormOwnProps & { className?: string; readonly?: boolean };

export type PriceAdjustmentProps = PriceAdjustmentStateProps & PriceAdjustmentDispatchProps & PriceAdjustmentOwnProps;

const mapStateToProps = (state: AppState, { formId }: PriceAdjustmentOwnProps): PriceAdjustmentStateProps => {
  return {
    priceType: (getLiveQuotePriceType(state, formId) ?? liveReferentialSelectors.getPriceType(state, formId))!,
    canAdjustPrice: liveSelectors.canAdjustPrice(state, formId),
    currency: liveReferentialSelectors.getCurrency(state, formId)!,
    comment: liveFormInputSelectors.getFormComment(state, formId),
    tradingPrice: getLiveQuoteTradingPriceFormatted(state, formId)!,
    xMarginPlusUpfront: getLiveQuoteXMarginPlusUpfront(state, formId)!,
    customizedTradingPriceAdjustment: liveFormInputSelectors.getFormInputCustomizedTradingPriceAdjustment(
      state,
      formId,
    ),
    customizedUpfront: liveFormInputSelectors.getFormInputCustomizedUpfront(state, formId),
    customizedXMarginAdjustment: liveFormInputSelectors.getFormInputCustomizedXMarginAdjustment(state, formId),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
  { formId: tabId }: PriceAdjustmentOwnProps,
): PriceAdjustmentDispatchProps => {
  return {
    onCommentChanged: value => {
      dispatch(liveInputFormActions.formFieldChanged(tabId, 'comment', value));
    },
    onCustomizedXMarginAdjustmentChanged: (value?: number) => {
      dispatch(liveInputFormActions.formFieldChanged(tabId, 'customizedXMarginAdjustment', value));
    },
    onCustomizedTradingPriceAdjustmentChanged: (value?: number) => {
      dispatch(liveInputFormActions.formFieldChanged(tabId, 'customizedTradingPriceAdjustment', value));
    },
    onCustomizedUpfrontChanged: (value?: number) => {
      dispatch(liveInputFormActions.formFieldChanged(tabId, 'customizedUpfront', value));
    },
    onResetCustomizedUpfront: () => {
      dispatch(liveThunks.resetCustomizedUpfrontThunk(tabId));
    },
    onResetCustomizedXMargin: () => {
      dispatch(liveThunks.resetCustomizedXMarginThunk(tabId));
    },
  };
};

export const connectPriceAdjustment = connect(mapStateToProps, mapDispatchToProps);

export const ConnectedPriceAdjustment = connectPriceAdjustment(PriceAdjustment);
